/** @file IK dependency loops over document ids (§6.6): chains that move bones the others read, round. */
import type { Doc } from './schema';

/** Constraint ids in compile order: ascending `order`, ties in document order (compileIk's order). */
function compileOrder(doc: Doc): string[] {
  return Object.keys(doc.constraints).sort((a, b) => (doc.constraints[a]?.order ?? 0) - (doc.constraints[b]?.order ?? 0));
}

/** Calls `visit` on `bone` and each ancestor, at most `limit` bones (a parent loop is CYCLE in validateDoc already). */
function upFrom(doc: Doc, bone: string | null, limit: number, visit: (id: string) => void): void {
  let steps = 0;
  for (let b = bone; b !== null && steps <= limit; b = doc.bones[b]?.parent ?? null, steps++) visit(b);
}

/**
 * For each constraint (compile order), the others it depends on: those whose chain holds its target or the parent of
 * its first bone, or an ancestor of either (the compiler's sortIkChains relation, over document ids).
 */
function dependencies(doc: Doc, order: readonly string[], limit: number): number[][] {
  const owners = new Map<string, number[]>();
  order.forEach((id, k) => {
    for (const bone of doc.constraints[id]?.bones ?? []) owners.set(bone, [...(owners.get(bone) ?? []), k]);
  });
  return order.map((id, k) => {
    const c = doc.constraints[id];
    if (!c) return [];
    const deps = new Set<number>();
    const read = (bone: string): void => { for (const j of owners.get(bone) ?? []) if (j !== k) deps.add(j); };
    upFrom(doc, c.target, limit, read);
    upFrom(doc, doc.bones[c.bones[0]]?.parent ?? null, limit, read);
    return [...deps];
  });
}

/** True when the target lies strictly below the constraint's own chain (a target that is a chain bone is IK_INVALID). */
function movesOwnTarget(doc: Doc, id: string, limit: number): boolean {
  const c = doc.constraints[id];
  if (!c || c.bones.includes(c.target)) return false;
  let inside = false;
  upFrom(doc, doc.bones[c.target]?.parent ?? null, limit, (bone) => { inside ||= c.bones.includes(bone); });
  return inside;
}

/** Iterative depth-first walk from `root` (state: 0 unseen, 1 on the path, 2 done); a back edge adds its loop. */
function walkFrom(root: number, deps: readonly (readonly number[])[], state: Uint8Array, loops: number[][]): void {
  const path = [root];
  const next = [0];
  state[root] = 1;
  while (path.length > 0) {
    const top = path.length - 1;
    const u = path[top] ?? 0;
    const edges = deps[u] ?? [];
    const e = next[top] ?? 0;
    if (e >= edges.length) { state[u] = 2; path.pop(); next.pop(); continue; }
    next[top] = e + 1;
    const v = edges[e] ?? 0;
    if (state[v] === 1) loops.push(path.slice(path.indexOf(v)));
    else if (state[v] === 0) { state[v] = 1; path.push(v); next.push(0); }
  }
}

/** Every loop a depth-first walk meets (one per back edge), as constraint indices in dependency order. */
function loopsOf(deps: readonly (readonly number[])[]): number[][] {
  const state = new Uint8Array(deps.length);
  const loops: number[][] = [];
  for (let root = 0; root < deps.length; root++) if (state[root] === 0) walkFrom(root, deps, state, loops);
  return loops;
}

/**
 * The IK dependency loops of `doc` as constraint-id lists (the first id is not repeated): `[id]` for a target strictly
 * below its own chain, then the loops across constraints, each read "the first depends on the second ... the last on
 * the first", walked in compile order. Empty when the update order exists (spec §6.6). O(constraints × depth).
 */
export function ikCycles(doc: Doc): string[][] {
  const order = compileOrder(doc);
  if (order.length === 0) return [];
  const limit = Object.keys(doc.bones).length;
  const self = order.filter((id) => movesOwnTarget(doc, id, limit)).map((id) => [id]);
  const loops = loopsOf(dependencies(doc, order, limit)).map((loop) => loop.map((k) => order[k] ?? ''));
  return [...self, ...loops];
}
